import { getExtensionValue, getReferenceString, MedplumClient, WithId } from "@medplum/core";
import { CodeableConcept, Coding, HealthcareService, Location } from "@medplum/fhirtypes";

import camelCase from "lodash/camelCase";

import { Extensions } from "@/fhir/enums/extensions";
import { Systems } from "@/fhir/enums/systems";
import { joinReferenceString, joinTokenString } from "@/utils/medplum";

import {
  FetchDefaultServiceDateTimezoneOptions,
  ServiceDateTimezoneResolution,
} from "../interfaces/service-date-timezone";
import { getBrowserServiceDateTimezone } from "../utils/browser-timezone";
import { getOrganizationDefaultTimezone } from "../utils/organization-default-timezone";

import { fetchOrganizationLocationHoursWithAvailability } from "./location-hours";

/**
 * Reads the IANA timezone coding from a Location Hours HealthcareService.
 *
 * @param locationHoursService Location hours service that may store a timezone extension.
 * @returns Timezone coding when configured.
 */
export function getTimezoneCodingFromLocationHoursService(
  locationHoursService: HealthcareService | undefined,
): Coding | undefined {
  const timezoneExtension = getExtensionValue(locationHoursService, Extensions.Timezone) as
    | CodeableConcept
    | undefined;

  const coding = timezoneExtension?.coding?.[0];
  return coding?.code ? coding : undefined;
}

/**
 * Resolves the IANA timezone used to convert an appointment instant into wall-clock
 * hours for availability matching. Location hours are timezone-naive (`08:00`–`18:00`);
 * the conversion must use this location timezone rather than a hardcoded default.
 *
 * @param locationHoursService Location-hours HealthcareService that may store a timezone extension.
 * @param fallbackTimezone IANA timezone used when the location has no configured timezone.
 * @returns IANA timezone code for wall-clock availability comparison.
 */
export function resolveAvailabilityTimezoneFromLocationHours(
  locationHoursService: HealthcareService | undefined,
  fallbackTimezone: string,
): string {
  return getTimezoneCodingFromLocationHoursService(locationHoursService)?.code ?? fallbackTimezone;
}

/**
 * Fetches active practice locations for an organization.
 *
 * @param medplum Medplum client used for FHIR search.
 * @param organizationId Practice organization id.
 * @returns Active Location resources for the organization.
 */
export async function fetchOrganizationActiveLocations(
  medplum: MedplumClient,
  organizationId: string,
) {
  return medplum.searchResources("Location", {
    organization: joinReferenceString("Organization", organizationId),
    status: "active",
  });
}

/**
 * Reads the distinct timezones configured on location-hours services.
 *
 * Locations that share a timezone contribute one coding, so a practice whose locations all use the
 * same timezone resolves to that single timezone.
 *
 * @param locationHoursServices Location-hours services with configured availability.
 * @returns One timezone coding per distinct IANA code, in first-seen order.
 */
export function getConfiguredLocationHoursTimezones(
  locationHoursServices: HealthcareService[],
): Coding[] {
  return locationHoursServices.reduce<Coding[]>((timezones, locationHoursService) => {
    const timezone = getTimezoneCodingFromLocationHoursService(locationHoursService);
    if (timezone && !timezones.some((existing) => existing.code === timezone.code)) {
      timezones.push(timezone);
    }
    return timezones;
  }, []);
}

/**
 * Fetches the Location Hours HealthcareService for a location reference.
 *
 * @param medplum Medplum client used for FHIR search.
 * @param locationReference FHIR location reference (for example Location/abc).
 * @returns Matching location hours service, if any.
 */
export async function fetchLocationHoursForLocation(
  medplum: MedplumClient,
  locationReference: string,
): Promise<HealthcareService | undefined> {
  return medplum.searchOne("HealthcareService", {
    location: locationReference,
    "service-category": joinTokenString(Systems.QH, camelCase("Location Hours")),
  });
}

/**
 * Resolves the timezone configured for a specific practice location.
 *
 * @param medplum Medplum client used for FHIR search.
 * @param locationReference FHIR location reference (for example Location/abc).
 * @returns IANA timezone coding when the location has hours configured.
 */
export async function fetchTimezoneForLocationReference(
  medplum: MedplumClient,
  locationReference: string,
): Promise<Coding | undefined> {
  const locationHours = await fetchLocationHoursForLocation(medplum, locationReference);
  return getTimezoneCodingFromLocationHoursService(locationHours);
}

/**
 * Resolves the FHIR location reference for a client-scheduled appointment.
 *
 * @param location Calendar link location resource when present.
 * @returns Location reference string (for example Location/abc) when the location has an id.
 */
export function resolveLocationReferenceForClientAppointment(
  location: WithId<Location> | undefined,
): string | undefined {
  if (!location?.id) {
    return undefined;
  }

  return getReferenceString(location);
}

/**
 * Resolves the IANA timezone for a client-scheduled appointment from its calendar link location.
 *
 * @param medplum Medplum client used for FHIR search.
 * @param location Calendar link location resource when present.
 * @returns Timezone coding configured on the location's location-hours service.
 */
export async function fetchTimezoneForClientAppointmentLocation(
  medplum: MedplumClient,
  location: WithId<Location> | undefined,
): Promise<Coding | undefined> {
  const locationReference = resolveLocationReferenceForClientAppointment(location);
  if (!locationReference) {
    return undefined;
  }

  return fetchTimezoneForLocationReference(medplum, locationReference);
}

/**
 * Resolves the timezone for a practitioner based on their first assigned location.
 *
 * @param medplum Medplum client used for FHIR search.
 * @param organizationId Practice organization id.
 * @param practitionerId Practitioner id creating the encounter.
 * @returns IANA timezone coding when the practitioner's location has hours configured.
 */
export async function fetchPractitionerTimezone(
  medplum: MedplumClient,
  organizationId: string,
  practitionerId: string,
): Promise<Coding | undefined> {
  const practitionerRole = await medplum.searchOne("PractitionerRole", {
    practitioner: joinReferenceString("Practitioner", practitionerId),
    organization: joinReferenceString("Organization", organizationId),
    active: true,
  });

  const locationReference = practitionerRole?.location?.[0]?.reference;
  if (!locationReference) {
    return undefined;
  }

  return fetchTimezoneForLocationReference(medplum, locationReference);
}

/**
 * Resolves the default service-date timezone for a practice.
 *
 * The practice's own default timezone wins and marks the result as practice-configured. Without one,
 * a single configured location-hours timezone is used directly and marks the result the same way.
 * Zero or several distinct timezones fall back to the viewer's browser timezone
 * (America/Los_Angeles when the browser reports none) and mark the result as not practice-configured.
 *
 * @param locationHoursTimezones Distinct timezones configured on location-hours services.
 * @param organizationTimezone The practice's default timezone, when it has one.
 * @returns The IANA timezone coding and whether the practice configuration settled it.
 */
export function resolveDefaultServiceDateTimezone(
  locationHoursTimezones: Coding[],
  organizationTimezone?: Coding,
): ServiceDateTimezoneResolution {
  if (organizationTimezone) {
    return { timezone: organizationTimezone, isPracticeConfigured: true };
  }
  if (locationHoursTimezones.length === 1) {
    return { timezone: locationHoursTimezones[0], isPracticeConfigured: true };
  }
  return { timezone: getBrowserServiceDateTimezone(), isPracticeConfigured: false };
}

/**
 * Resolves the default service-date timezone for encounter, scheduling, and billing inputs.
 *
 * The lookup order is described on `resolveDefaultServiceDateTimezone`. The practice default lives on
 * the Organization, so it is read, and can fail the lookup, only when `usePracticeDefault` is set.
 *
 * @param medplum Medplum client used for FHIR reads and search.
 * @param organizationId Practice organization id.
 * @param options Set `usePracticeDefault` to let the practice default timezone win (billing does).
 * @returns The IANA timezone coding and whether the practice configuration settled it.
 */
export async function fetchDefaultServiceDateTimezone(
  medplum: MedplumClient,
  organizationId: string,
  options: FetchDefaultServiceDateTimezoneOptions = {},
): Promise<ServiceDateTimezoneResolution> {
  if (options.usePracticeDefault) {
    // A fresh read keeps every biller on the latest default after a Manager changes it.
    const organization = await medplum.readResource("Organization", organizationId, {
      cache: "no-cache",
    });
    const organizationTimezone = getOrganizationDefaultTimezone(organization);
    // A valid practice default settles the lookup, so a failed Location Hours search cannot block it.
    if (organizationTimezone) return resolveDefaultServiceDateTimezone([], organizationTimezone);
  }

  const locationHoursServices = await fetchOrganizationLocationHoursWithAvailability(
    medplum,
    organizationId,
  );
  return resolveDefaultServiceDateTimezone(
    getConfiguredLocationHoursTimezones(locationHoursServices),
  );
}

/**
 * @deprecated Use fetchDefaultServiceDateTimezone instead.
 */
export async function fetchPracticeTimezone(
  medplum: MedplumClient,
  organizationId: string,
): Promise<Coding> {
  const { timezone } = await fetchDefaultServiceDateTimezone(medplum, organizationId);
  return timezone;
}
